import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment-timezone'
import '!style-loader!css-loader!react-big-calendar/lib/css/react-big-calendar.css' // eslint-disable-line
import s from './Calendar.scss'
import * as ACT from './actions'
import { isAdmin, getUser } from 'utils/authService'
import Select from 'react-select'
import { fromUsers } from 'utils/serializers'
import { If, Checkbox } from 'components/ideal-core-ui'
import { ScheduleTaskModal } from 'components/common/modals/ScheduleTaskModal'
import { AddEventModal } from './AddEventModal'
import {
  IoIosCall,
  IoIosMail,
  IoIosPeople,
  IoMdCafe,
  IoMdRestaurant,
  IoIosPaper
} from 'react-icons/io'
import { FaCheck, FaInfoCircle, FaPlusCircle } from 'react-icons/fa'
import { eventStyleGetter } from './helpers'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import '!style-loader!css-loader!react-big-calendar/lib/addons/dragAndDrop/styles.css' // eslint-disable-line
import { PageTitle } from 'components/common/PageTitle'
import { utcDate, utcCurrentDate, hasModule } from 'utils/helpers'
import cn from 'classnames'
import { useImmer } from 'use-immer'
import { UncontrolledTooltip } from 'reactstrap'

const IconMapper = {
  CALL: <IoIosCall />,
  EMAIL: <IoIosMail />,
  TASK: <IoIosPaper />,
  MEET: <IoIosPeople />,
  LUNCH: <IoMdCafe />,
  DINNER: <IoMdRestaurant />
}

const PrefixMapper = {
  task: 'TSK - ',
  event: 'CAL - ',
  bid: 'BID ',
  followup: 'FLW ',
  lead: 'LEAD '
}

const initialEvent = {
  users: [],
  contacts: [],
  createdBy: null,
  description: '',
  type: '',
  start: moment(utcCurrentDate()).toDate(),
  end: moment(utcCurrentDate()).add(1, 'hours').toDate(),
  location: '',
  status: '',
  subject: '',
  notifications: {
    time: 10,
    type: {
      value: 'MINUTE',
      label: 'Minutes'
    }
  },
  reminders: {
    value: false,
    label: 'No Reminder'
  }
}

const localizer = momentLocalizer(moment)
const DragAndDropCalendar = withDragAndDrop(Calendar)

const CalendarContainer = () => {
  const dispatch = useDispatch()
  const tasks = useSelector(state => state.calendar.tasks)
  const events = useSelector(state => state.calendar.events)
  const bids = useSelector(state => state.calendar.bids)
  const leads = useSelector(state => state.calendar.leads)
  const followUps = useSelector(state => state.calendar.followUps)
  const users = useSelector(state => state.users.users)
  const [showScheduleTaskModal, setShowScheduleTaskModal] = useState(false)
  const [showAddEventModal, setShowAddEventModal] = useState(false)
  const [task, setTask] = useState(null)
  const [event, setEvent] = useImmer(initialEvent)
  const user = getUser()
  const loggedUserId = user?.id
  const loggedUserName = user?.name
  const [filters, setFilters] = useState({
    bids: true,
    followUps: true,
    tasks: true,
    events: true,
    leads: true,
    bidType: { value: 'all', label: 'All' }
  })
  const [calendarEvents, setCalendarEvents] = useState([])
  const hasLeadsModule = hasModule('leads')
  const [dateRange, setDateRange] = useState(null)
  const [usersFilter, setUsersFilter] = useState([
    {
      value: loggedUserId,
      label: loggedUserName
    }
  ])

  useEffect(() => {
    dispatch({ type: ACT.USERS_FETCH_REQUESTED })
  }, [dispatch])

  const fetchCalendarTasksAndEvents = useCallback(() => {
    dispatch({
      type: ACT.CALENDAR_TASKS_FETCH_REQUESTED
    })

    dispatch({
      type: ACT.CALENDAR_EVENTS_FETCH_REQUESTED
    })
  }, [dispatch])

  const fetchCalendarBidsAndFollowups = useCallback(() => {
    dispatch({
      type: ACT.CALENDAR_BIDS_FETCH_REQUESTED,
      payload: {
        id: isAdmin() ? loggedUserId : ''
      }
    })

    if (hasLeadsModule) {
      dispatch({
        type: ACT.CALENDAR_LEADS_FETCH_REQUESTED,
        payload: {
          id: isAdmin() ? loggedUserId : '',
          users
        }
      })
    }

    dispatch({
      type: ACT.CALENDAR_FOLLOWUPS_FETCH_REQUESTED,
      payload: {
        id: isAdmin() ? loggedUserId : ''
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, loggedUserId, users])

  useEffect(() => {
    fetchCalendarTasksAndEvents()
    fetchCalendarBidsAndFollowups()
  }, [fetchCalendarBidsAndFollowups, fetchCalendarTasksAndEvents])

  useEffect(() => {
    let allEvents = []
    if (filters.bids) {
      allEvents = [...allEvents, ...bids]
    }
    if (hasLeadsModule && filters.leads) {
      allEvents = [...allEvents, ...leads]
    }
    if (filters.tasks) {
      allEvents = [...allEvents, ...tasks]
    }
    if (filters.events) {
      allEvents = [...allEvents, ...events]
    }
    if (filters.followUps) {
      allEvents = [...allEvents, ...followUps]
    }
    if (filters.bidType.value !== 'all') {
      allEvents = allEvents.filter(e => e[filters.bidType.value])
    }
    setCalendarEvents(allEvents)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tasks,
    events,
    bids,
    leads,
    followUps,
    filters.bids,
    filters.tasks,
    filters.events,
    filters.followUps,
    filters.bidType,
    filters.leads
  ])

  const filterCalendarTasksAndEventsByUser = users => {
    const ids = users.map(u => u.value)
    dispatch({
      type: ACT.CALENDAR_TASKS_BY_USER_FETCH_REQUESTED,
      payload: {
        ids
      }
    })

    dispatch({
      type: ACT.CALENDAR_EVENTS_BY_USER_FETCH_REQUESTED,
      payload: {
        ids
      }
    })
  }

  const filterCalendarBidsAndFollowupsByUser = (users, dates) => {
    const ids = users.map(u => u.value)

    dispatch({
      type: ACT.CALENDAR_FOLLOWUPS_BY_USER_FETCH_REQUESTED,
      payload: {
        ids,
        dateRange: dates ? dates : dateRange,
        isAdmin: isAdmin()
      }
    })

    dispatch({
      type: ACT.CALENDAR_BIDS_BY_USER_FETCH_REQUESTED,
      payload: {
        ids,
        dateRange: dates ? dates : dateRange,
        isAdmin: isAdmin()
      }
    })

    if (hasLeadsModule) {
      dispatch({
        type: ACT.CALENDAR_LEADS_BY_USER_FETCH_REQUESTED,
        payload: {
          ids,
          users
        }
      })
    }
  }

  const updateTaskOnDragAndResize = (event, start, end, type, droppedOnAllDaySlot) => {
    if (droppedOnAllDaySlot) {
      start = moment(start).startOf('day')
      end = moment(end).startOf('day')
    }

    if (event.allDay && !droppedOnAllDaySlot && type === 'move') {
      end = moment(start).endOf('day')
    }

    event.task.startDate = utcDate(start)
    event.task.dueDate = utcDate(end)

    dispatch({
      type: ACT.UPDATE_DEAL_TASK_REQUESTED,
      payload: {
        task: event.task
      }
    })
  }

  const updateEventOnDragAndResize = (event, start, end, type, droppedOnAllDaySlot) => {
    if (droppedOnAllDaySlot) {
      start = moment(start).startOf('day')
      end = moment(end).startOf('day')
    }

    if (event.allDay && !droppedOnAllDaySlot && type === 'move') {
      end = moment(start).endOf('day')
    }

    dispatch({
      type: ACT.CALENDAR_EVENT_UPDATE_REQUESTED,
      payload: {
        event: { ...event, start, end }
      }
    })
  }

  const moveEvent = ({ event, start, end, isAllDay: droppedOnAllDaySlot }) => {
    if (['bid', 'followup'].includes(event.type)) {
      dispatch({
        type: ACT.DEAL_DATE_UPDATE_REQUESTED,
        payload: {
          date: end,
          id: event.id,
          followUpView: event.type === 'followup'
        }
      })

      dispatch({
        type: ACT.CALENDAR_DEAL_DATE_UPDATE,
        payload: {
          date: end,
          id: event.id,
          followUpView: event.type === 'followup'
        }
      })
    } else {
      if (event.task) {
        updateTaskOnDragAndResize(event, start, end, 'move', droppedOnAllDaySlot)
      } else {
        updateEventOnDragAndResize(event, start, end, 'move', droppedOnAllDaySlot)
      }
    }
  }

  const resizeEvent = ({ event, start, end }) => {
    if (event.task) {
      updateTaskOnDragAndResize(event, start, end, 'resize')
    } else {
      updateEventOnDragAndResize(event, start, end, 'resize')
    }
  }

  const handleSelect = ({ start, end }) => {
    toggleAddEventModal()
    setEvent(draft => {
      draft.start = moment(start).toDate()
      if (moment(end).isAfter(moment(start))) {
        draft.end = moment(end).toDate()
      } else {
        draft.end = moment(start).add(1, 'hours').toDate()
      }
    })
  }

  const toggleScheduleTaskModal = () => {
    setShowScheduleTaskModal(showScheduleTaskModal => !showScheduleTaskModal)
  }

  const toggleAddEventModal = () => {
    clearAddEventModal()
    setShowAddEventModal(showAddEventModal => !showAddEventModal)
  }

  const handleEventClick = event => {
    if (['bid', 'followup'].includes(event.type)) {
      window.open(`${window.location.origin}/opportunities/${event.id}`, '_blank')
    } else if (event.type === 'lead') {
      window.open(`${window.location.origin}/leads/${event.id}`, '_blank')
    } else {
      if (event.task) {
        setTask(event.task)
        toggleScheduleTaskModal()
      } else {
        toggleAddEventModal()
        setEvent(event)
      }
    }
  }

  const updateTask = task => {
    dispatch({
      type: ACT.UPDATE_DEAL_TASK_REQUESTED,
      payload: {
        task
      }
    })
  }

  const deleteTask = task => {
    dispatch({
      type: ACT.DELETE_DEAL_TASK_REQUESTED,
      payload: {
        task
      }
    })
  }

  const updateEvent = params => {
    dispatch({
      type: ACT.CALENDAR_EVENT_UPDATE_REQUESTED,
      payload: {
        event: { ...event, sendInviteEmail: params.sendInviteEmail }
      }
    })
    toggleAddEventModal()
  }

  const deleteEvent = () => {
    dispatch({
      type: ACT.CALENDAR_EVENT_DELETE_REQUESTED,
      payload: {
        id: event.id
      }
    })
    toggleAddEventModal()
  }

  const saveEvent = params => {
    dispatch({
      type: ACT.CALENDAR_EVENT_SAVE_REQUESTED,
      payload: {
        event: { ...event, sendInviteEmail: params.sendInviteEmail }
      }
    })
    toggleAddEventModal()
  }

  const clearAddEventModal = () => {
    setEvent(initialEvent)
  }

  const markTaskAsDone = id => {
    dispatch({
      type: ACT.DEAL_TASK_MARK_COMPLETED_REQUESTED,
      payload: {
        id
      }
    })
  }

  const markTaskAsNotDone = id => {
    dispatch({
      type: ACT.DEAL_TASK_MARK_NOT_COMPLETED_REQUESTED,
      payload: {
        id
      }
    })
  }

  const addEventIcon = event => {
    return (
      <>
        {event.task?.dateCompleted ? <FaCheck /> : ''}
        {event.type ? PrefixMapper[event.type] : ''}
        {event.task ? IconMapper[event.task.type] : ''}
        {event.title}
      </>
    )
  }

  const onRangeChange = range => {
    setDateRange(range)
    filterCalendarBidsAndFollowupsByUser(usersFilter, range)
  }

  return (
    <>
      <PageTitle view="Calendar" />
      <div className={s.wrapper} data-cy="calendar-wrapper">
        {isAdmin() && (
          <>
            <Select
              onChange={values => {
                setUsersFilter(values)
                filterCalendarTasksAndEventsByUser(values)
                filterCalendarBidsAndFollowupsByUser(values)
              }}
              options={fromUsers(users)}
              className={s.userSelect}
              defaultValue={{
                value: loggedUserId,
                label: loggedUserName
              }}
              isMulti
            />
            <FaInfoCircle id="Type1" style={{ marginLeft: 10 }} />
            <UncontrolledTooltip placement="top" target="Type1">
              To see all entries on the calendar, please remove all users from selection.
            </UncontrolledTooltip>
          </>
        )}
        <div
          className={cn(s.filters, {
            [s.nonAdmin]: !isAdmin()
          })}
        >
          <div className={s.filter}>
            <Select
              value={filters.bidType}
              onChange={value =>
                setFilters(state => ({
                  ...state,
                  bidType: value
                }))
              }
              isDisabled={!filters.bids && !filters.followUps}
              options={[
                {
                  value: 'all',
                  label: 'All'
                },
                {
                  value: 'hardBid',
                  label: 'Hard Bid'
                },
                {
                  value: 'sealedBid',
                  label: 'Sealed Bid'
                }
              ]}
              className={s.bidTypeSelect}
            />
          </div>
          <div className={s.filter}>
            <Checkbox
              isChecked={filters.bids}
              triggerCheckbox={value =>
                setFilters(state => ({
                  ...state,
                  bids: value.target.checked
                }))
              }
              cy="filter-bids"
            />
            <span className={s.label}>Bid Due Dates</span>
          </div>
          <div className={s.filter}>
            <Checkbox
              isChecked={filters.followUps}
              triggerCheckbox={value =>
                setFilters(state => ({
                  ...state,
                  followUps: value.target.checked
                }))
              }
              cy="filter-followUps"
            />
            <span className={s.label}>Follow-up Dates</span>
          </div>
          <div className={s.filter}>
            <Checkbox
              isChecked={filters.tasks}
              triggerCheckbox={value =>
                setFilters(state => ({
                  ...state,
                  tasks: value.target.checked
                }))
              }
            />
            <span className={s.label}>Tasks</span>
          </div>
          {hasLeadsModule && (
            <div className={s.filter}>
              <Checkbox
                isChecked={filters.leads}
                triggerCheckbox={value =>
                  setFilters(state => ({
                    ...state,
                    leads: value.target.checked
                  }))
                }
              />
              <span className={s.label}>Leads</span>
            </div>
          )}
          <div className={s.filter}>
            <Checkbox
              isChecked={filters.events}
              triggerCheckbox={value =>
                setFilters(state => ({
                  ...state,
                  events: value.target.checked
                }))
              }
            />
            <span className={s.label}>Calendar Events</span>
          </div>
          {/* TODO Add check to display this button */}
          <div className={cn(s.filter, s.addEvent)} onClick={toggleAddEventModal}>
            <FaPlusCircle /> Add Calendar Event
          </div>
        </div>
        <DragAndDropCalendar
          onRangeChange={onRangeChange}
          selectable
          onSelectEvent={handleEventClick}
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          titleAccessor={event => addEventIcon(event)}
          eventPropGetter={eventStyleGetter}
          onEventDrop={moveEvent}
          resizable
          onEventResize={resizeEvent}
          onSelectSlot={handleSelect}
          resizableAccessor={event => {
            if (['bid', 'followup', 'lead'].includes(event.type)) {
              return false
            }
            return true
          }}
          draggableAccessor={event => {
            if (['bid', 'lead'].includes(event.type)) return false
            return true
          }}
        />
        <p className={s.note}>
          *Drag and drop is not available for BID and LEAD events on the calendar
        </p>
      </div>
      <If condition={showScheduleTaskModal}>
        <ScheduleTaskModal
          showScheduleTaskModal={showScheduleTaskModal}
          toggleScheduleTaskModal={toggleScheduleTaskModal}
          updateTask={updateTask}
          users={fromUsers(users)}
          edit
          selectedTask={task}
          markAsDone={markTaskAsDone}
          markAsNotDone={markTaskAsNotDone}
          deleteTask={deleteTask}
          company={
            !task
              ? undefined
              : task.dealId
                ? undefined
                : { value: task.companyId, label: task.companyName }
          }
        />
      </If>
      <If condition={showAddEventModal}>
        <AddEventModal
          showAddEventModal={showAddEventModal}
          toggleAddEventModal={toggleAddEventModal}
          saveEvent={saveEvent}
          event={event}
          setEvent={setEvent}
          users={fromUsers(users)}
          updateEvent={updateEvent}
          deleteEvent={deleteEvent}
        />
      </If>
    </>
  )
}

export default CalendarContainer
